import {
  SegmentedControl,
  SegmentedControlItem,
} from '@pypestream/design-system';
import { FC } from 'react';

import { parseViewMode, type WorkflowsViewMode } from './workflows-view-mode';

import './view-mode-toggle.scss';

interface ViewModeToggleProps {
  /**
   * Where the toggle is being rendered. `floating` overlays the toolbar band
   * (Test View, which has no toolbar of its own); `toolbar` sits inside the
   * controls bar as an ordinary flex item (Table View).
   */
  variant?: 'floating' | 'toolbar';
  value: WorkflowsViewMode;
  onChange: (next: WorkflowsViewMode) => void;
  /**
   * Render the Test View segment. Driven by `bobcat-test-view`, and passed in
   * rather than read here so this stays a presentational control with no flag
   * dependency of its own — the page already resolves the flags to decide
   * WHICH view to render, and the segment must agree with that decision.
   */
  showTestView?: boolean;
  /**
   * Render the Canvas segment. Driven by `bobcat-build-canvas`, same
   * pass-in-rather-than-read pattern as `showTestView`.
   */
  showCanvasView?: boolean;
}

/**
 * "Table View / Test View / Canvas" switch.
 *
 * Relocated from `pages/agent-view/agent-view.tsx` 2026-09-23 when Agent
 * View was deleted — this control was never Agent-View-specific, it is
 * `builder.tsx`'s shared view-mode chrome, so it moved out to its own file
 * rather than dying with the page. The Agent View segment (`showAgentView`)
 * is gone along with it.
 */
export const ViewModeToggle: FC<ViewModeToggleProps> = ({
  value,
  onChange,
  variant = 'floating',
  showTestView = false,
  showCanvasView = false,
}) => (
  // Two placements, because the two views need different things:
  //
  // - `floating` (Test View) — wrapped and lifted. `<Table>` paints
  //   `.c-editor-table-bg` (`position: fixed; inset: 0`, 82% opaque white) and
  //   `.c-editor-table-container` (`position: absolute; inset: 0`, opaque),
  //   both with `z-index: auto`. A static in-flow toggle rendered before the
  //   table is painted OVER by them and cannot be clicked, so Test View would
  //   be unreachable. Positioning the wrapper puts it in the same stacking
  //   context with a real z-index.
  // - `toolbar` (Table View) — a REAL flex child of the controls bar, so it
  //   takes up width and the toolbar's other items reflow around it. The
  //   floating placement only ever worked while the middle of the toolbar
  //   stayed empty; once the auto-renumber toggle moved into that space the
  //   two drew on top of each other.
  <div className={`c-agent-view-toggle c-agent-view-toggle--${variant}`}>
    <SegmentedControl
      // Shrinks the track to its segments. Without it the DS default is a
      // FULL-WIDTH track whose segments share the space equally, which renders
      // as an oversized control with empty regions. Declared on the WC
      // as `fitContent` (attribute `fit-content`); same form as
      // `default-messages.tsx`.
      fitContent
      test-id="bobcat-agent-view-mode"
      value={value}
      // A real parse, not a coercion. `detail.value` is typed `string`, and
      // a `=== 'agent' ? 'agent' : 'table'` form was correct only while there
      // were two segments — with a third it reported a Test View click as
      // "table".
      onChange={(e: CustomEvent<{ value: string }>) =>
        onChange(parseViewMode(e.detail.value))
      }
    >
      <SegmentedControlItem value="table">Table View</SegmentedControlItem>
      {showTestView && (
        <SegmentedControlItem value="test">Test View</SegmentedControlItem>
      )}
      {showCanvasView && (
        <SegmentedControlItem value="canvas">Canvas</SegmentedControlItem>
      )}
    </SegmentedControl>
  </div>
);
